import { MANAGED_CONFIG_VENDOR_CLAUDE_CODE } from "../../lib/managedConfigClient.js";
import { POLICY_CATEGORIES } from "./claudeCode/copy.js";
import { cloneControls, composeManagedAgentConfig, controlsFromDocument } from "./claudeCode/document.js";
import { deriveEditorValues, incompleteLists } from "./claudeCode/editorValues.js";
import { Authentication, MinimumVersion, ModelAvailability, ProvidersBackend, StartingBehavior } from "./claudeCode/SessionSetup.jsx";
import { AdvancedPermissions, ManagedPermissionRules } from "./claudeCode/ToolPermissions.jsx";
import { AutoModeEnvironment, ClassifierDecisions, PermitAutoMode, ReviewEveryShellCommand } from "./claudeCode/AutoMode.jsx";
import { BrowserControls, CrossMachineMessaging, PublishedOutput, RemoteDevSsh, RemoteManagement } from "./claudeCode/ExternalRemote.jsx";
import { AdminMessages } from "./claudeCode/Messaging.jsx";
import {
    ExecutionRestrictions,
    ManagedHookPolicy,
    MarketplacePolicy,
    McpServerPolicy,
    SkillSources,
} from "./claudeCode/ContentResources.jsx";

function categoryLabel(id) {
    return POLICY_CATEGORIES.find((category) => category.id === id).label;
}

const autoModeOn = (controls) => controls.autoModeAllowed;

/// One supported coding agent. The page reads every category, subsection,
/// control, and document function from this entry. `pointers` lists the JSON
/// pointer prefixes a subsection writes; a change is counted in the first
/// subsection whose prefix covers it.
export const CLAUDE_CODE_AGENT = Object.freeze({
    id: "claude-code",
    vendor: MANAGED_CONFIG_VENDOR_CLAUDE_CODE,
    label: "Claude Code",
    // `agent_provider` of this agent's rows in endpoint config state.
    configProvider: "anthropic",
    emptyControls: () => cloneControls(),
    clone: cloneControls,
    fromDocument: controlsFromDocument,
    compose: composeManagedAgentConfig,
    incompleteLists,
    derive: deriveEditorValues,
    // Arrays whose members belong to different subsections.
    memberPointers: ["/strictPluginOnlyCustomization"],
    // Accepted settings with no control. A loaded value is written back
    // unchanged; the tally counts a change to one of them separately.
    preservedPointers: [
        "/permissions/additionalDirectories",
        "/disableClaudeAiConnectors",
        "/requiredMinimumVersion",
        "/requiredMaximumVersion",
        "/cleanupPeriodDays",
        // Accepted by the catalog, staged for upcoming category passes. Each
        // moves to its own subsection as that category is built.
        "/allowClaudeInChromeWithManagedMcp",
        "/disableDesktopLocalSessions",
        "/disableMobileSimulatorTools",
        "/disableDeepLinkRegistration",
    ],
    // Catalog containers. Their members are counted, not the container.
    containerPointers: ["/permissions", "/autoMode"],
    categories: [
        {
            id: "session",
            label: categoryLabel("session"),
            subsections: [
                { id: "authentication", label: "Authentication", pointers: ["/forceLoginMethod", "/forceLoginOrgUUID"], Component: Authentication },
                { id: "providers-backend", label: "Providers & backend", pointers: ["/allowedProviders", "/forceLoginGatewayUrl"], Component: ProvidersBackend },
                { id: "starting-behavior", label: "Starting behavior", pointers: ["/permissions/defaultMode", "/model", "/permissions/disableBypassPermissionsMode"], Component: StartingBehavior },
                { id: "model-availability", label: "Model availability", pointers: ["/availableModels", "/enforceAvailableModels", "/deniedModels", "/availableModelsMatch"], Component: ModelAvailability },
                { id: "minimum-version", label: "Minimum version", pointers: ["/minimumVersion"], Component: MinimumVersion },
            ],
        },
        {
            id: "permissions",
            label: categoryLabel("permissions"),
            subsections: [
                { id: "managed-rules", label: "Managed permission rules", pointers: ["/permissions/deny", "/permissions/ask", "/permissions/allow"], Component: ManagedPermissionRules },
                { id: "permissions-advanced", label: "Advanced", pointers: ["/allowManagedPermissionRulesOnly"], Component: AdvancedPermissions },
            ],
        },
        {
            id: "auto",
            label: categoryLabel("auto"),
            subsections: [
                { id: "permit-auto", label: "Permit auto mode", pointers: ["/permissions/disableAutoMode"], Component: PermitAutoMode },
                { id: "auto-environment", label: "Describe your environment", pointers: ["/autoMode/environment"], Component: AutoModeEnvironment, visible: autoModeOn },
                { id: "auto-decisions", label: "Customize classifier decisions", pointers: ["/autoMode/hard_deny", "/autoMode/soft_deny", "/autoMode/allow"], Component: ClassifierDecisions, visible: autoModeOn },
                { id: "auto-shell", label: "Review every shell command", pointers: ["/autoMode/classifyAllShell"], Component: ReviewEveryShellCommand, visible: autoModeOn },
            ],
        },
        {
            id: "inputs",
            label: categoryLabel("inputs"),
            subsections: [
                {
                    id: "mcp-servers",
                    label: "MCP server policy",
                    pointers: ["/allowedMcpServers", "/deniedMcpServers", "/allowManagedMcpServersOnly", "/allowAllClaudeAiMcps", "/managedMcpServers", { pointer: "/strictPluginOnlyCustomization", members: ["mcp"] }],
                    Component: McpServerPolicy,
                },
                { id: "marketplaces", label: "Plugin marketplace policy", pointers: ["/strictKnownMarketplaces", "/blockedMarketplaces", "/disableCommandPluginSources", "/disableSideloadFlags"], Component: MarketplacePolicy },
                { id: "skill-sources", label: "Skill and subagent sources", pointers: [{ pointer: "/strictPluginOnlyCustomization", exceptMembers: ["mcp"] }], Component: SkillSources },
                { id: "hooks", label: "Managed hook policy", pointers: ["/allowManagedHooksOnly", "/allowedHttpHookUrls", "/httpHookAllowedEnvVars"], Component: ManagedHookPolicy },
                { id: "execution", label: "Execution restrictions", pointers: ["/disableSkillShellExecution", "/disableWorkflows", "/disableBundledSkills"], Component: ExecutionRestrictions },
            ],
        },
        {
            id: "external",
            label: categoryLabel("external"),
            subsections: [
                { id: "remote-management", label: "Remote management", pointers: ["/disableRemoteControl"], Component: RemoteManagement },
                { id: "cross-machine", label: "Cross-machine messaging", pointers: ["/isolatePeerMachines"], Component: CrossMachineMessaging },
                { id: "remote-ssh", label: "Remote dev (SSH)", pointers: ["/sshHostAllowlist"], Component: RemoteDevSsh },
                { id: "published-output", label: "Published output", pointers: ["/disableArtifact", "/enableArtifact"], Component: PublishedOutput },
                { id: "browser", label: "Browser", pointers: ["/disableBrowserExternalNavigation", "/browserExternalPageTools"], Component: BrowserControls },
            ],
        },
        {
            id: "messaging",
            label: categoryLabel("messaging"),
            subsections: [
                { id: "admin-messages", label: "Admin messages", pointers: ["/pluginTrustMessage", "/companyAnnouncements"], Component: AdminMessages },
            ],
        },
    ],
});

/// Supported agents, in tab order. An agent appears here only once the API
/// accepts its vendor.
export const AGENT_DEFINITIONS = Object.freeze([CLAUDE_CODE_AGENT]);
